import {
  PREDICAMENT_STATE_NO,
  PREDICAMENT_STATE_YES,
  REHAB_FORM_NAMES,
} from "core/consts";
import {
  Button,
  ControlledDateInput,
  ControlledInput,
  ControlledRadioGroup,
} from "ds/ui";
import { parseAddress } from "dsl/ecosystems/PatientForms/RehabForms/GKVGeneralForm";
import { DownloadIcon } from "lucide-react";
import { useEffect } from "react";
import { useFormContext, useWatch } from "react-hook-form";
import { FormFieldConfigProps } from "..";
import { SourceWrapper } from "../../utils";

export const CommunicationInGermanPossibleField = ({
  setSourceSelected,
  skipRequiredValidation,
  sourceSelected,
  translations,
}: FormFieldConfigProps<typeof REHAB_FORM_NAMES.GKV_GENERAL>) => {
  const { setValue } = useFormContext();
  const communicationInGermanPossible = useWatch({
    name: "communication_in_german_possible",
  });

  const options = [
    {
      label: translations.actions.yes,
      value: PREDICAMENT_STATE_YES,
    },
    {
      label: translations.actions.no,
      subForm: () => (
        <SourceWrapper
          fieldName="communication_other_language"
          isIconOnly
          setSourceSelected={setSourceSelected}
          sourceSelected={sourceSelected}
        >
          <ControlledInput
            className="pt-4"
            label={
              translations.patientForms.generalForm.sectionTwo
                .communicationNoGerman
            }
            name="communication_other_language"
            placeholder=" "
          />
        </SourceWrapper>
      ),
      value: PREDICAMENT_STATE_NO,
    },
  ];

  useEffect(() => {
    if (communicationInGermanPossible === PREDICAMENT_STATE_YES) {
      setValue("communication_other_language", "");
    }
  }, [communicationInGermanPossible, setValue]);

  return (
    <ControlledRadioGroup
      isRequired={!skipRequiredValidation}
      label={
        translations.patientForms.generalForm.sectionTwo
          .communicationWithPatient
      }
      name="communication_in_german_possible"
      options={options}
    />
  );
};

export const PreferredClinicContactedField = ({
  auction,
  setSourceSelected,
  skipRequiredValidation,
  sourceSelected,
  translations,
}: FormFieldConfigProps<typeof REHAB_FORM_NAMES.GKV_GENERAL>) => {
  const { resetField, setValue } = useFormContext();
  const preferedClinicContacted = useWatch({
    name: "prefered_clinic_contacted",
  });

  const handleProviderImport = () => {
    if (!auction?.winner?.careprovider) return;

    const { careprovider } = auction.winner;
    const { address, central_phone_number, name } = careprovider;

    const { houseNumber, street } = address?.street
      ? { houseNumber: address.street_number, street: address.street }
      : parseAddress(address?.address ?? null);

    setValue("prefered_clinic_name", name ?? null);
    setValue("prefered_clinic_street", street ?? null);
    setValue("prefered_clinic_house_number", houseNumber ?? null);
    setValue("prefered_clinic_postal_code", address?.zip_code ?? null);
    setValue("prefered_clinic_city", address?.city ?? null);
    setValue("prefered_clinic_phone", central_phone_number ?? null);
    setValue(
      "prefered_clinic_contacted_start_date",
      auction.start_date ?? null,
    );
  };

  const options = [
    {
      label: translations.actions.no,
      value: PREDICAMENT_STATE_NO,
    },
    {
      label: translations.actions.yes,
      subForm: () => (
        <div className="flex flex-col gap-2">
          {auction?.winner && (
            <Button
              color="primary"
              id="import-provider-from-recare"
              startContent={<DownloadIcon className="h-4 w-4" />}
              variant="ghost"
              onPress={handleProviderImport}
            >
              {translations.patientForms.generalForm.importProviderInformation}
            </Button>
          )}
          <SourceWrapper
            fieldName="prefered_clinic_name"
            isIconOnly
            setSourceSelected={setSourceSelected}
            sourceSelected={sourceSelected}
          >
            <ControlledInput
              isRequired={!skipRequiredValidation}
              label={translations.institution.name}
              name="prefered_clinic_name"
              placeholder=" "
            />
          </SourceWrapper>
          <SourceWrapper
            fieldName="prefered_clinic_street"
            isIconOnly
            setSourceSelected={setSourceSelected}
            sourceSelected={sourceSelected}
          >
            <ControlledInput
              isRequired={!skipRequiredValidation}
              label={translations.address.street}
              name="prefered_clinic_street"
              placeholder=" "
            />
          </SourceWrapper>
          <SourceWrapper
            fieldName="prefered_clinic_house_number"
            isIconOnly
            setSourceSelected={setSourceSelected}
            sourceSelected={sourceSelected}
          >
            <ControlledInput
              isRequired={!skipRequiredValidation}
              label={translations.address.number}
              name="prefered_clinic_house_number"
              placeholder=" "
            />
          </SourceWrapper>
          <SourceWrapper
            fieldName="prefered_clinic_postal_code"
            isIconOnly
            setSourceSelected={setSourceSelected}
            sourceSelected={sourceSelected}
          >
            <ControlledInput
              isRequired={!skipRequiredValidation}
              label={translations.address.zipcode}
              name="prefered_clinic_postal_code"
              placeholder=" "
            />
          </SourceWrapper>
          <SourceWrapper
            fieldName="prefered_clinic_city"
            isIconOnly
            setSourceSelected={setSourceSelected}
            sourceSelected={sourceSelected}
          >
            <ControlledInput
              isRequired={!skipRequiredValidation}
              label={translations.address.city}
              name="prefered_clinic_city"
              placeholder=" "
            />
          </SourceWrapper>
          <SourceWrapper
            fieldName="prefered_clinic_phone"
            isIconOnly
            setSourceSelected={setSourceSelected}
            sourceSelected={sourceSelected}
          >
            <ControlledInput
              isRequired={!skipRequiredValidation}
              label={translations.contactInformation.phoneNumber}
              name="prefered_clinic_phone"
              placeholder=" "
            />
          </SourceWrapper>
          <SourceWrapper
            fieldName="prefered_clinic_contacted_start_date"
            isIconOnly
            setSourceSelected={setSourceSelected}
            sourceSelected={sourceSelected}
          >
            <ControlledDateInput
              isRequired={!skipRequiredValidation}
              label={
                translations.patientForms.generalForm.sectionTwo.rehabIntakeDate
              }
              name="prefered_clinic_contacted_start_date"
            />
          </SourceWrapper>
        </div>
      ),
      value: PREDICAMENT_STATE_YES,
    },
  ];

  useEffect(() => {
    if (preferedClinicContacted === PREDICAMENT_STATE_NO) {
      setValue("prefered_clinic_name", "");
      setValue("prefered_clinic_street", "");
      setValue("prefered_clinic_house_number", "");
      setValue("prefered_clinic_postal_code", "");
      setValue("prefered_clinic_city", "");
      setValue("prefered_clinic_phone", "");
      // TODO: not resetting the date picker value correctly
      resetField("prefered_clinic_contacted_start_date", {
        defaultValue: null,
      });
    }
  }, [preferedClinicContacted, setValue, resetField]);

  return (
    <ControlledRadioGroup
      isRequired={!skipRequiredValidation}
      label={translations.patientForms.generalForm.sectionTwo.costCoverage}
      name="prefered_clinic_contacted"
      options={options}
    />
  );
};
